import React, { FC } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import noop from 'lodash/noop';
import { LastPage } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { secondsToString } from '../../../utils/timeStringFormat';
import { BLACK, BLUE } from '../../../constants/theme';
import { selectCurrentTime } from '../../../state/videoPlayer';
import { selectIsMultipleClipReviewInProgress } from '../../../state/currentPlayingClip';
import {
  updateCurrentEditingClipIndex,
  resetCurrentEditingClipIndex,
  selectIsClipEditing,
  updateLastEditedClipIndex,
} from '../../../state/currentEditingClip';
import {
  MarkTime,
  navigateToMarkIOTime,
  setClipMarkTime,
} from '../../../state/clipList';
import { IAppState } from '../../../../../state/reducers';
import { IVideoClip } from '../../../../../api/vault/clipList';

const Container = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const MarkIOContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 60%;
  align-items: center;
  color: ${BLACK};
`;

const MarkIOTitle = styled.span`
  flex: 1;
  display: flex;
  padding-bottom: 5px;
  align-items: center;
  font-size: 10px;
`;

interface IMarkIOTimeProps { disabled?: boolean }

const MarkIOTime = styled.span<IMarkIOTimeProps>`
  flex: 1;
  display: flex;
  align-items: center;
  cursor: ${(props) => (!props.disabled && 'pointer')};
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (!props.disabled && BLUE)};

  &:hover {
    text-decoration: ${(props) => (!props.disabled && 'underline')};
  }
`;

const MarkIconContainer = styled.div`
  width: 34px;
  button {
    padding: 6px;
  }
`;

const MarkIconFlippedContainer = styled.div`
  width: 34px;
  button {
    padding: 6px;
  }
  transform: scaleX(-1);
`;

interface IOwnProps {
  clipIndex: number;
  clip: IVideoClip;
}

interface IStateProps {
  currentTime: number;
  markIOTimeDisabled: boolean;
  isEditing: boolean;
  isSharedClip: boolean;
}

interface IDispatchProps {
  navigateToMarkIOTime: (time: number, clipIndex: number) => void;
  setClipMarkTime: (clipIndex: number, time: MarkTime) => void;
  updateCurrentEditingClipIndex: (index: number) => void;
  resetCurrentEditingClipIndex: () => void;
  updateLastEditedClipIndex: (index: number) => void;
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps { }

const ClipItemMiddle: FC<IProps> = (props: IProps) => {
  const isMarkDisabled = (time: MarkTime) => {
    const {
      currentTime,
      clip,
      isSharedClip,
    } = props;
    if (isSharedClip) return true;

    const { markInTime, markOutTime } = clip;
    if (time === MarkTime.In) return markOutTime !== null && currentTime >= markOutTime;
    return markInTime === null || currentTime <= markInTime;
  };

  const handleMarkClick = (time: MarkTime) => {
    const {
      clipIndex,
      setClipMarkTime,
      resetCurrentEditingClipIndex,
    } = props;
    setClipMarkTime(clipIndex, time);
    resetCurrentEditingClipIndex();
  };

  const handleMarkIOTimeClick = (clipTime: number) => {
    const {
      navigateToMarkIOTime,
      updateCurrentEditingClipIndex,
      clipIndex,
    } = props;
    navigateToMarkIOTime(clipTime, clipIndex);
    updateCurrentEditingClipIndex(clipIndex);
  };

  const {
    clip,
    markIOTimeDisabled,
    isEditing,
    isSharedClip,
  } = props;
  const { markInTime, markOutTime } = clip;
  const markInTimeString = secondsToString(markInTime);
  const markOutTimeString = secondsToString(markOutTime);
  return (
    <Container>
      <MarkIconContainer>
        {(!isSharedClip && isEditing) && (
          <IconButton
            disabled={isMarkDisabled(MarkTime.In)}
            onClick={isMarkDisabled(MarkTime.In) ? noop : () => handleMarkClick(MarkTime.In)}
          >
            <LastPage />
          </IconButton>
        )}
      </MarkIconContainer>
      <MarkIOContainer style={{ paddingRight: '5px' }}>
        <MarkIOTitle>In:</MarkIOTitle>
        <MarkIOTime
          disabled={markIOTimeDisabled}
          onClick={markIOTimeDisabled ? noop : () => handleMarkIOTimeClick(markInTime)}
        >
          {markInTimeString}
        </MarkIOTime>
      </MarkIOContainer>
      <MarkIOContainer>
        <MarkIOTitle>Out:</MarkIOTitle>
        <MarkIOTime
          disabled={markIOTimeDisabled}
          onClick={markIOTimeDisabled ? noop : () => handleMarkIOTimeClick(markOutTime)}
        >
          {markOutTimeString}
        </MarkIOTime>
      </MarkIOContainer>
      <MarkIconFlippedContainer>
        {(!isSharedClip && isEditing) && (
          <IconButton
            disabled={isMarkDisabled(MarkTime.Out)}
            onClick={isMarkDisabled(MarkTime.Out) ? noop : () => handleMarkClick(MarkTime.Out)}
          >
            <LastPage />
          </IconButton>
        )}
      </MarkIconFlippedContainer>
    </Container>
  );
};

const mapStateToProps = (state: IAppState, ownProps: IOwnProps): IStateProps => ({
  currentTime: selectCurrentTime(state),
  markIOTimeDisabled: !!ownProps.clip.userId || selectIsMultipleClipReviewInProgress(state),
  isEditing: selectIsClipEditing(state, ownProps.clipIndex),
  isSharedClip: !!ownProps.clip.userId,
});

const mapDispatchToProps: IDispatchProps = {
  navigateToMarkIOTime,
  setClipMarkTime,
  updateCurrentEditingClipIndex,
  resetCurrentEditingClipIndex,
  updateLastEditedClipIndex,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ClipItemMiddle);
